import { useCallback, useEffect, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../ui/switch";
import { FoundingBackfillDialog } from "./FoundingBackfillDialog";

/**
 * Founding window controls for /admin/founding:
 * the open/closed switch (every gift joins the wall while open) and the
 * "Add past gifts to the wall" backfill. `client` is the caller's authed axios.
 */
export const FoundingWindowPanel = ({ client, onChanged }) => {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await client.get("/admin/founding/window");
      setState(r.data);
    } catch {
      setState({ open: false, error: true });
    }
  }, [client]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (next) => {
    setSaving(true);
    try {
      const r = await client.put("/admin/founding/window", { open: next });
      setState(r.data);
      toast.success(next ? "Founding window open — every new gift joins the wall." : "Founding window closed — new gifts no longer join the wall.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the founding window.");
    } finally {
      setSaving(false);
    }
  };

  const isOpen = Boolean(state?.open);

  return (
    <div className="mt-8 rounded-2xl border border-border bg-bone" data-testid="founding-window-panel">
      <div className="flex flex-col gap-5 px-5 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${isOpen ? "bg-yellow text-ink" : "bg-ink/8 text-olive"}`}>
            <Sparkles className="h-4 w-4" strokeWidth={2} />
          </span>
          <div>
            <p className="font-semibold text-ink">
              Founding window{" "}
              <span
                className={`ml-1 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ring-1 ring-inset ${
                  isOpen ? "bg-grass/15 text-grass ring-grass/30" : "bg-ink/8 text-olive ring-ink/15"
                }`}
                data-testid="founding-window-status"
              >
                {state === null ? "…" : isOpen ? "open" : "closed"}
              </span>
            </p>
            <p className="mt-1 max-w-xl text-[13px] leading-relaxed text-olive">
              While open, every gift made anywhere on the site (a student&rsquo;s campaign or the General Fund)
              puts the donor on this wall automatically and emails them a private edit link. Close it when the beta launch ends.
              {state?.updated_at && (
                <span className="block text-[11px] text-olive/70">
                  Last changed {new Date(state.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  {state.updated_by ? ` by ${state.updated_by}` : ""}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-4 sm:pl-6">
          <button
            type="button"
            onClick={() => setBackfillOpen(true)}
            data-testid="founding-backfill-open"
            className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 bg-bone px-4 py-2 text-[12px] font-semibold text-ink transition hover:bg-beige"
          >
            Add past gifts to the wall
          </button>
          <label className="flex items-center gap-2 text-[12px] font-semibold text-ink">
            {saving ? <Loader2 className="h-4 w-4 animate-spin text-olive" /> : null}
            <Switch
              checked={isOpen}
              disabled={saving || state === null}
              onCheckedChange={(v) => toggle(Boolean(v))}
              data-testid="founding-window-switch"
              aria-label="Founding window open"
            />
          </label>
        </div>
      </div>
      <FoundingBackfillDialog open={backfillOpen} onOpenChange={setBackfillOpen} client={client} onDone={onChanged} />
    </div>
  );
};
